import {
  type KilnChecksheetChecklistData,
  type KilnCheckSheetChecklistDto
} from '@domain/hroc-checklists'

import type {KilnCheckSheetChecklistFormValues} from '../types'

import {
  mapYesNoFormValuesToItem,
  mapYesNoItemToFormItem,
  mapYesNoFormValueToBoolean,
  mapBooleanToYesNoFormValue
} from './yes-no.mappers'

const mapCoolerPrimaryDtoToForm = (
  section: NonNullable<KilnChecksheetChecklistData['coolerSection']>
) => ({
  coolerDrivesOperatingProperly: {
    value: mapBooleanToYesNoFormValue(section.coolerDrivesOperatingProperly.value),
    valueRetake: mapBooleanToYesNoFormValue(section.coolerDrivesOperatingProperlyRetake ?? false),
    comment: section.coolerDrivesOperatingProperly.comment || ''
  },
  coolerDrives3ShotsGrease: {
    value: mapBooleanToYesNoFormValue(section.coolerDrives3ShotsGrease.value),
    valueRetake: mapBooleanToYesNoFormValue(section.coolerDrives3ShotsGreaseRetake ?? false),
    comment: section.coolerDrives3ShotsGrease.comment || ''
  },
  megaBlastersOperatingProperly: {
    value: mapBooleanToYesNoFormValue(section.megaBlastersOperatingProperly.value),
    valueRetake: mapBooleanToYesNoFormValue(section.megaBlastersOperatingProperlyRetake ?? false),
    comment: section.megaBlastersOperatingProperly.comment || ''
  }
})

const mapCoolerSectionDtoToFormValues = (
  coolerSection: KilnChecksheetChecklistData['coolerSection'] | undefined
) => {
  if (!coolerSection) return null

  return {
    compartmentsNormal: coolerSection.compartmentsNormal || '',
    compartmentsFull: coolerSection.compartmentsFull || '',
    compartmentsNormalRetake: coolerSection.compartmentsNormalRetake || '',
    compartmentsFullRetake: coolerSection.compartmentsFullRetake || '',
    compartmentsComment: coolerSection.compartmentsComment || '',

    gratesNormal: coolerSection.gratesNormal || '',
    gratesMissing: coolerSection.gratesMissing || '',
    gratesNormalRetake: coolerSection.gratesNormalRetake || '',
    gratesMissingRetake: coolerSection.gratesMissingRetake || '',
    gratesComment: coolerSection.gratesComment || '',

    ...mapCoolerPrimaryDtoToForm(coolerSection),
    undergrateFans1To8OperatingProperly: mapYesNoItemToFormItem(
      coolerSection.undergrateFans1To8OperatingProperly
    ),
    clinkerBreakerBeltsBearingsMotorOperatingProperly: mapYesNoItemToFormItem(
      coolerSection.clinkerBreakerBeltsBearingsMotorOperatingProperly
    ),
    plattcoValvesOperatingProperly: mapYesNoItemToFormItem(
      coolerSection.plattcoValvesOperatingProperly
    ),
    plattcoValvesAnyLeftOpen: mapYesNoItemToFormItem(coolerSection.plattcoValvesAnyLeftOpen),
    finesDrags6119And6120OperatingProperly: mapYesNoItemToFormItem(
      coolerSection.finesDrags6119And6120OperatingProperly
    ),
    panConveyorEastPanOperatingProperly: mapYesNoItemToFormItem(
      coolerSection.panConveyorEastPanOperatingProperly
    ),
    panConveyorEastPanExcessiveSpillage: mapYesNoItemToFormItem(
      coolerSection.panConveyorEastPanExcessiveSpillage
    ),
    panConveyorEastPanPansNormal: coolerSection.panConveyorEastPanPansNormal,
    panConveyorEastPanPansDamaged: coolerSection.panConveyorEastPanPansDamaged,
    panConveyorEastPanPansComment: coolerSection.panConveyorEastPanPansComment
  }
}

const mapCoolerBaghouseSectionDtoToFormValues = (
  coolerBaghouseSection: KilnChecksheetChecklistData['coolerBaghouseSection'] | undefined
) => {
  if (!coolerBaghouseSection) return null

  return {
    baghouseFan5930DamageLeaks: mapYesNoItemToFormItem(
      coolerBaghouseSection.baghouseFan5930DamageLeaks
    ),
    screwsDragsElevatorDamageLeaks: mapYesNoItemToFormItem(
      coolerBaghouseSection.screwsDragsElevatorDamageLeaks
    )
  }
}

const mapKilnSectionDtoToFormValues = (
  kilnSection: KilnChecksheetChecklistData['kilnSection'] | undefined
) => {
  if (!kilnSection) return null

  return {
    slippageCreepChecks1st: kilnSection.slippageCreepChecks1st || '',
    slippageCreepChecks2nd: kilnSection.slippageCreepChecks2nd || '',
    slippageCreepChecks3rd: kilnSection.slippageCreepChecks3rd || '',
    slippageCreepChecks1stRetake: kilnSection.slippageCreepChecks1stRetake || '',
    slippageCreepChecks2ndRetake: kilnSection.slippageCreepChecks2ndRetake || '',
    slippageCreepChecks3rdRetake: kilnSection.slippageCreepChecks3rdRetake || '',
    slippageCreepChecksComment: kilnSection.slippageCreepChecksComment || '',
    kilnBearingsOilLevelLevelsOk: mapYesNoItemToFormItem(kilnSection.kilnBearingsOilLevelLevelsOk),
    kilnBearingsCoolingWaterFlowSufficientFlow: mapYesNoItemToFormItem(
      kilnSection.kilnBearingsCoolingWaterFlowSufficientFlow
    ),
    kilnThrustRollerOilLevelLevelsOk: mapYesNoItemToFormItem(
      kilnSection.kilnThrustRollerOilLevelLevelsOk
    ),
    kilnThrustRollerKilnPositionOnThrust: kilnSection.kilnThrustRollerKilnPositionOnThrust,
    kilnThrustRollerKilnPositionFloating: kilnSection.kilnThrustRollerKilnPositionFloating,
    kilnThrustRollerKilnPositionUphill: kilnSection.kilnThrustRollerKilnPositionUphill,
    kilnThrustRollerKilnPositionComment: kilnSection.kilnThrustRollerKilnPositionComment,
    kilnFeedEndSealPlatesBoltsCablesNormal: kilnSection.kilnFeedEndSealPlatesBoltsCablesNormal,
    kilnFeedEndSealPlatesBoltsCablesDeficiency:
      kilnSection.kilnFeedEndSealPlatesBoltsCablesDeficiency,
    kilnFeedEndSealPlatesBoltsCablesComment: kilnSection.kilnFeedEndSealPlatesBoltsCablesComment,
    kilnFeedEndSealMaterialLeak: mapYesNoItemToFormItem(kilnSection.kilnFeedEndSealMaterialLeak),
    kilnDischargeSealPlatesCablesShroudNormal:
      kilnSection.kilnDischargeSealPlatesCablesShroudNormal,
    kilnDischargeSealPlatesCablesShroudDamaged:
      kilnSection.kilnDischargeSealPlatesCablesShroudDamaged,
    kilnDischargeSealPlatesCablesShroudComment:
      kilnSection.kilnDischargeSealPlatesCablesShroudComment,
    kilnDischargeSealMaterialLeakExcessiveSpillage: mapYesNoItemToFormItem(
      kilnSection.kilnDischargeSealMaterialLeakExcessiveSpillage
    ),
    noseRingNoseCastingsNormal: kilnSection.noseRingNoseCastingsNormal,
    noseRingNoseCastingsDamaged: kilnSection.noseRingNoseCastingsDamaged,
    noseRingNoseCastingsComment: kilnSection.noseRingNoseCastingsComment,
    noseRingNoseRingFansOperatingProperly: mapYesNoItemToFormItem(
      kilnSection.noseRingNoseRingFansOperatingProperly
    ),
    noseRingFansGreased3ShotsOfGrease: mapYesNoItemToFormItem(
      kilnSection.noseRingFansGreased3ShotsOfGrease
    ),
    shellFansBeltsBearingsMotorOperatingProperly: mapYesNoItemToFormItem(
      kilnSection.shellFansBeltsBearingsMotorOperatingProperly
    )
  }
}

const mapHousekeepingSectionDtoToFormValues = (
  housekeepingSection: KilnChecksheetChecklistData['housekeepingSection'] | undefined
) => {
  if (!housekeepingSection) return null

  return {...housekeepingSection}
}

const mapAdditionalCommentsSectionDtoToFormValues = (
  additionalCommentsSection: KilnChecksheetChecklistData['additionalCommentsSection'] | undefined
) => {
  if (!additionalCommentsSection) return null

  return {
    checkLinesForOilLeaksOrSpills: mapBooleanToYesNoFormValue(
      additionalCommentsSection.checkLinesForOilLeaksOrSpills
    ),
    checkLinesForOilLeaksOrSpillsActionTaken:
      additionalCommentsSection.checkLinesForOilLeaksOrSpillsActionTaken,
    jackhammerInServerRoom: mapYesNoItemToFormItem(
      additionalCommentsSection.jackhammerInServerRoom
    ),
    jackhammerInCabinetByClinkerBreaker: mapYesNoItemToFormItem(
      additionalCommentsSection.jackhammerInCabinetByClinkerBreaker
    )
  }
}

export const mapKilnCheckSheetChecklistDtoToFormValues = ({
  data,
  kind
}: KilnCheckSheetChecklistDto): KilnCheckSheetChecklistFormValues => {
  return {
    kind,
    coolerSection: mapCoolerSectionDtoToFormValues(data?.coolerSection),
    coolerBaghouseSection: mapCoolerBaghouseSectionDtoToFormValues(data?.coolerBaghouseSection),
    kilnSection: mapKilnSectionDtoToFormValues(data?.kilnSection),
    housekeepingSection: mapHousekeepingSectionDtoToFormValues(data?.housekeepingSection),
    additionalCommentsSection: mapAdditionalCommentsSectionDtoToFormValues(
      data?.additionalCommentsSection
    )
  }
}

// eslint-disable-next-line complexity
const mapCoolerSectionFormValuesToDto = (
  coolerSection: KilnCheckSheetChecklistFormValues['coolerSection']
) => {
  if (!coolerSection) return null

  return {
    compartmentsNormal: coolerSection.compartmentsNormal || '',
    compartmentsFull: coolerSection.compartmentsFull || '',
    compartmentsNormalRetake: coolerSection.compartmentsNormalRetake || '',
    compartmentsFullRetake: coolerSection.compartmentsFullRetake || '',
    compartmentsComment: coolerSection.compartmentsComment || '',
    gratesNormal: coolerSection.gratesNormal || '',
    gratesMissing: coolerSection.gratesMissing || '',
    gratesNormalRetake: coolerSection.gratesNormalRetake || '',
    gratesMissingRetake: coolerSection.gratesMissingRetake || '',
    gratesComment: coolerSection.gratesComment || '',
    coolerDrivesOperatingProperly: {
      value: mapYesNoFormValueToBoolean(coolerSection.coolerDrivesOperatingProperly.value),
      valueRetake: mapYesNoFormValueToBoolean(
        coolerSection.coolerDrivesOperatingProperly.valueRetake
      ),
      comment:
        (coolerSection.coolerDrivesOperatingProperly.comment || '').trim() === ''
          ? null
          : (coolerSection.coolerDrivesOperatingProperly.comment || '').trim()
    },
    coolerDrives3ShotsGrease: {
      value: mapYesNoFormValueToBoolean(coolerSection.coolerDrives3ShotsGrease.value),
      valueRetake: mapYesNoFormValueToBoolean(coolerSection.coolerDrives3ShotsGrease.valueRetake),
      comment:
        (coolerSection.coolerDrives3ShotsGrease.comment || '').trim() === ''
          ? null
          : (coolerSection.coolerDrives3ShotsGrease.comment || '').trim()
    },
    megaBlastersOperatingProperly: {
      value: mapYesNoFormValueToBoolean(coolerSection.megaBlastersOperatingProperly.value),
      valueRetake: mapYesNoFormValueToBoolean(
        coolerSection.megaBlastersOperatingProperly.valueRetake
      ),
      comment:
        (coolerSection.megaBlastersOperatingProperly.comment || '').trim() === ''
          ? null
          : (coolerSection.megaBlastersOperatingProperly.comment || '').trim()
    },
    undergrateFans1To8OperatingProperly: mapYesNoFormValuesToItem(
      coolerSection.undergrateFans1To8OperatingProperly
    ),
    clinkerBreakerBeltsBearingsMotorOperatingProperly: mapYesNoFormValuesToItem(
      coolerSection.clinkerBreakerBeltsBearingsMotorOperatingProperly
    ),
    plattcoValvesOperatingProperly: mapYesNoFormValuesToItem(
      coolerSection.plattcoValvesOperatingProperly
    ),
    plattcoValvesAnyLeftOpen: mapYesNoFormValuesToItem(coolerSection.plattcoValvesAnyLeftOpen),
    finesDrags6119And6120OperatingProperly: mapYesNoFormValuesToItem(
      coolerSection.finesDrags6119And6120OperatingProperly
    ),
    panConveyorEastPanOperatingProperly: mapYesNoFormValuesToItem(
      coolerSection.panConveyorEastPanOperatingProperly
    ),
    panConveyorEastPanExcessiveSpillage: mapYesNoFormValuesToItem(
      coolerSection.panConveyorEastPanExcessiveSpillage
    ),
    panConveyorEastPanPansNormal: coolerSection.panConveyorEastPanPansNormal,
    panConveyorEastPanPansDamaged: coolerSection.panConveyorEastPanPansDamaged,
    panConveyorEastPanPansComment: coolerSection.panConveyorEastPanPansComment
  }
}

const mapCoolerBaghouseSectionFormValuesToDto = (
  coolerBaghouseSection: KilnCheckSheetChecklistFormValues['coolerBaghouseSection']
) => {
  if (!coolerBaghouseSection) return null

  return {
    baghouseFan5930DamageLeaks: mapYesNoFormValuesToItem(
      coolerBaghouseSection.baghouseFan5930DamageLeaks
    ),
    screwsDragsElevatorDamageLeaks: mapYesNoFormValuesToItem(
      coolerBaghouseSection.screwsDragsElevatorDamageLeaks
    )
  }
}

const mapKilnSectionFormValuesToDto = (
  kilnSection: KilnCheckSheetChecklistFormValues['kilnSection']
) => {
  if (!kilnSection) return null

  return {
    slippageCreepChecks1st: kilnSection.slippageCreepChecks1st || '',
    slippageCreepChecks2nd: kilnSection.slippageCreepChecks2nd || '',
    slippageCreepChecks3rd: kilnSection.slippageCreepChecks3rd || '',
    slippageCreepChecks1stRetake: kilnSection.slippageCreepChecks1stRetake || '',
    slippageCreepChecks2ndRetake: kilnSection.slippageCreepChecks2ndRetake || '',
    slippageCreepChecks3rdRetake: kilnSection.slippageCreepChecks3rdRetake || '',
    slippageCreepChecksComment: kilnSection.slippageCreepChecksComment || '',
    kilnBearingsOilLevelLevelsOk: mapYesNoFormValuesToItem(
      kilnSection.kilnBearingsOilLevelLevelsOk
    ),
    kilnBearingsCoolingWaterFlowSufficientFlow: mapYesNoFormValuesToItem(
      kilnSection.kilnBearingsCoolingWaterFlowSufficientFlow
    ),
    kilnThrustRollerOilLevelLevelsOk: mapYesNoFormValuesToItem(
      kilnSection.kilnThrustRollerOilLevelLevelsOk
    ),
    kilnThrustRollerKilnPositionOnThrust: kilnSection.kilnThrustRollerKilnPositionOnThrust,
    kilnThrustRollerKilnPositionFloating: kilnSection.kilnThrustRollerKilnPositionFloating,
    kilnThrustRollerKilnPositionUphill: kilnSection.kilnThrustRollerKilnPositionUphill,
    kilnThrustRollerKilnPositionComment: kilnSection.kilnThrustRollerKilnPositionComment,
    kilnFeedEndSealPlatesBoltsCablesNormal: kilnSection.kilnFeedEndSealPlatesBoltsCablesNormal,
    kilnFeedEndSealPlatesBoltsCablesDeficiency:
      kilnSection.kilnFeedEndSealPlatesBoltsCablesDeficiency,
    kilnFeedEndSealPlatesBoltsCablesComment: kilnSection.kilnFeedEndSealPlatesBoltsCablesComment,
    kilnFeedEndSealMaterialLeak: mapYesNoFormValuesToItem(kilnSection.kilnFeedEndSealMaterialLeak),
    kilnDischargeSealPlatesCablesShroudNormal:
      kilnSection.kilnDischargeSealPlatesCablesShroudNormal,
    kilnDischargeSealPlatesCablesShroudDamaged:
      kilnSection.kilnDischargeSealPlatesCablesShroudDamaged,
    kilnDischargeSealPlatesCablesShroudComment:
      kilnSection.kilnDischargeSealPlatesCablesShroudComment,
    kilnDischargeSealMaterialLeakExcessiveSpillage: mapYesNoFormValuesToItem(
      kilnSection.kilnDischargeSealMaterialLeakExcessiveSpillage
    ),
    noseRingNoseCastingsNormal: kilnSection.noseRingNoseCastingsNormal,
    noseRingNoseCastingsDamaged: kilnSection.noseRingNoseCastingsDamaged,
    noseRingNoseCastingsComment: kilnSection.noseRingNoseCastingsComment,
    noseRingNoseRingFansOperatingProperly: mapYesNoFormValuesToItem(
      kilnSection.noseRingNoseRingFansOperatingProperly
    ),
    noseRingFansGreased3ShotsOfGrease: mapYesNoFormValuesToItem(
      kilnSection.noseRingFansGreased3ShotsOfGrease
    ),
    shellFansBeltsBearingsMotorOperatingProperly: mapYesNoFormValuesToItem(
      kilnSection.shellFansBeltsBearingsMotorOperatingProperly
    )
  }
}

const mapHousekeepingSectionFormValuesToDto = (
  housekeepingSection: KilnCheckSheetChecklistFormValues['housekeepingSection']
) => {
  if (!housekeepingSection) return null

  return {...housekeepingSection}
}

const mapAdditionalCommentsSectionFormValuesToDto = (
  additionalCommentsSection: KilnCheckSheetChecklistFormValues['additionalCommentsSection']
) => {
  if (!additionalCommentsSection) return null

  return {
    checkLinesForOilLeaksOrSpills: mapYesNoFormValueToBoolean(
      additionalCommentsSection.checkLinesForOilLeaksOrSpills
    ),
    checkLinesForOilLeaksOrSpillsActionTaken:
      additionalCommentsSection.checkLinesForOilLeaksOrSpillsActionTaken,
    jackhammerInServerRoom: mapYesNoFormValuesToItem(
      additionalCommentsSection.jackhammerInServerRoom
    ),
    jackhammerInCabinetByClinkerBreaker: mapYesNoFormValuesToItem(
      additionalCommentsSection.jackhammerInCabinetByClinkerBreaker
    )
  }
}

export const mapKilnChecksheetChecklistFormValuesToDataDto = ({
  coolerSection,
  coolerBaghouseSection,
  kilnSection,
  housekeepingSection,
  additionalCommentsSection
}: KilnCheckSheetChecklistFormValues): KilnChecksheetChecklistData => {
  return {
    coolerSection: mapCoolerSectionFormValuesToDto(
      coolerSection
    ) as unknown as KilnChecksheetChecklistData['coolerSection'],
    coolerBaghouseSection: mapCoolerBaghouseSectionFormValuesToDto(coolerBaghouseSection),
    kilnSection: mapKilnSectionFormValuesToDto(kilnSection),
    housekeepingSection: mapHousekeepingSectionFormValuesToDto(housekeepingSection),
    additionalCommentsSection:
      mapAdditionalCommentsSectionFormValuesToDto(additionalCommentsSection)
  }
}
